import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  KeyIcon, PencilSquareIcon, TrashIcon, XMarkIcon,
  CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon,
  ClipboardDocumentIcon, ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

interface SerperKey {
  id: string;
  label: string;
  api_key: string;
  is_active: boolean;
  credit_balance: number | null;
  last_used_at: string | null;
  failed_at: string | null;
}

export default function SerperKeys() {
  const [keys, setKeys] = useState<SerperKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addKey, setAddKey] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [editingKey, setEditingKey] = useState<SerperKey | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editApiKey, setEditApiKey] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/admin/serper-keys');
      setKeys(data);
    } catch {
      toast.error('Failed to load Serper API keys.');
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('Copied to clipboard.');
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await apiClient.post('/admin/serper-keys/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${data.created} key(s).${data.skipped > 0 ? ` ${data.skipped} skipped.` : ''}`);
      fetchKeys();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'label,api_key\nPrimary Key,your_serper_key_here\nBackup Key,another_serper_key_here\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'serper_keys_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addLabel.trim() || !addKey.trim()) {
      toast.error('Label and API key are required.');
      return;
    }
    setAddSubmitting(true);
    try {
      await apiClient.post('/admin/serper-keys', { label: addLabel.trim(), api_key: addKey.trim() });
      toast.success('Key added.');
      setAddOpen(false);
      setAddLabel('');
      setAddKey('');
      fetchKeys();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add key.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingKey) return;
    setEditSubmitting(true);
    try {
      const payload: Record<string, any> = { label: editLabel.trim(), is_active: editActive };
      if (editApiKey.trim() && editApiKey.trim() !== editingKey.api_key) {
        payload.api_key = editApiKey.trim();
      }
      await apiClient.put(`/admin/serper-keys/${editingKey.id}`, payload);
      toast.success('Key updated.');
      setEditingKey(null);
      fetchKeys();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update key.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleToggleActive = async (key: SerperKey) => {
    try {
      await apiClient.put(`/admin/serper-keys/${key.id}`, { is_active: !key.is_active });
      toast.success(key.is_active ? 'Key disabled.' : 'Key enabled.');
      fetchKeys();
    } catch {
      toast.error('Failed to update key.');
    }
  };

  const handleResetFailed = async (key: SerperKey) => {
    try {
      await apiClient.put(`/admin/serper-keys/${key.id}`, { is_active: true });
      toast.success('Key re-enabled.');
      fetchKeys();
    } catch {
      toast.error('Failed to re-enable key.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/admin/serper-keys/${id}`);
      toast.success('Key deleted.');
      setDeleteConfirm(null);
      fetchKeys();
    } catch {
      toast.error('Failed to delete key.');
    }
  };

  const openEdit = (key: SerperKey) => {
    setEditingKey(key);
    setEditLabel(key.label);
    setEditApiKey(key.api_key);
    setEditActive(key.is_active);
  };

  const fmtDate = (dt: string | null) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const activeCount = keys.filter(k => k.is_active && !k.failed_at).length;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto w-full flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Serper API Keys</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage the key pool — exhausted keys auto-rotate during searches.
            <span className="ml-2 text-emerald-600 font-semibold">{activeCount} active</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={importRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} />
          <button
            onClick={downloadTemplate}
            className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Download Template
          </button>
          <button
            onClick={() => importRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            {importing ? 'Importing…' : 'Import CSV'}
          </button>
          <button
            onClick={() => setAddOpen(true)}
            className="bg-brand-600 hover:bg-brand-700 text-slate-900 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
          >
            + Add Key
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[860px]">
            <thead>
              <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-50 bg-slate-50/40">
                <th className="px-6 py-3">Label</th>
                <th className="px-6 py-3">API Key</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Credits Left</th>
                <th className="px-6 py-3">Last Used</th>
                <th className="px-6 py-3">Failed At</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {[1,2,3,4,5,6,7].map(j => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 bg-slate-100 animate-pulse rounded" style={{ width: `${40 + (j % 3) * 20}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : keys.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <KeyIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">No Serper API keys yet. Add your first key to enable searches.</p>
                  </td>
                </tr>
              ) : (
                keys.map(key => {
                  const hasFailed = !!key.failed_at;
                  const isWorking = key.is_active && !hasFailed;
                  return (
                    <tr key={key.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-slate-900">{key.label}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-slate-600 select-all break-all">{key.api_key}</span>
                          <button
                            onClick={() => copyKey(key.api_key)}
                            className="p-1 hover:bg-slate-100 rounded transition-colors shrink-0"
                            title="Copy key"
                          >
                            <ClipboardDocumentIcon className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {hasFailed ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
                            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                            Exhausted
                          </span>
                        ) : isWorking ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                            <CheckCircleIcon className="w-3.5 h-3.5" />
                            Active
                          </span>
                        ) : (
                          <span className="text-[11px] font-bold text-slate-400">Disabled</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${key.credit_balance === null ? 'text-slate-400 italic' : key.credit_balance === 0 ? 'text-red-600 font-bold' : 'text-slate-700'}`}>
                          {key.credit_balance === null ? 'Unknown' : key.credit_balance === 0 ? '0 (empty)' : key.credit_balance.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-500">{fmtDate(key.last_used_at)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${hasFailed ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                          {fmtDate(key.failed_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {hasFailed && (
                            <button
                              onClick={() => handleResetFailed(key)}
                              title="Re-enable key"
                              className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                              <ArrowPathIcon className="w-4 h-4 text-emerald-600" />
                            </button>
                          )}
                          {!hasFailed && (
                            <button
                              onClick={() => handleToggleActive(key)}
                              className="px-2 py-1 hover:bg-slate-100 rounded-lg transition-colors text-[11px] font-bold text-slate-500"
                            >
                              {key.is_active ? 'Disable' : 'Enable'}
                            </button>
                          )}
                          <button onClick={() => openEdit(key)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                            <PencilSquareIcon className="w-4 h-4 text-slate-600" />
                          </button>
                          <button onClick={() => setDeleteConfirm(key.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                            <TrashIcon className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Add Serper API Key</h2>
              <button onClick={() => setAddOpen(false)} className="p-1 hover:bg-slate-100 rounded">
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Label *</label>
                <input
                  type="text"
                  value={addLabel}
                  onChange={e => setAddLabel(e.target.value)}
                  placeholder="e.g. Primary Key, Backup #2"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">API Key *</label>
                <input
                  type="text"
                  value={addKey}
                  onChange={e => setAddKey(e.target.value)}
                  placeholder="Paste your Serper API key"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-mono text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  autoComplete="off"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setAddOpen(false)} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={addSubmitting} className="flex-1 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-slate-900 text-sm font-bold rounded-xl transition-colors">
                  {addSubmitting ? 'Adding…' : 'Add Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Edit Key</h2>
              <button onClick={() => setEditingKey(null)} className="p-1 hover:bg-slate-100 rounded">
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Label *</label>
                <input
                  type="text"
                  value={editLabel}
                  onChange={e => setEditLabel(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">API Key</label>
                <input
                  type="text"
                  value={editApiKey}
                  onChange={e => setEditApiKey(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  autoComplete="off"
                />
                <p className="text-[11px] text-slate-400 mt-1.5">Leave unchanged to keep the existing key.</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit_active" checked={editActive} onChange={e => setEditActive(e.target.checked)} className="w-4 h-4 rounded border-slate-200 text-brand-600 focus:ring-brand-500" />
                <label htmlFor="edit_active" className="text-sm font-medium text-slate-700">Active</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditingKey(null)} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={editSubmitting} className="flex-1 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-slate-900 text-sm font-bold rounded-xl transition-colors">
                  {editSubmitting ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete API Key?</h3>
            <p className="text-sm text-slate-600 mb-6">This cannot be undone. The key will be removed from the rotation pool.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
